import React, { useEffect, useState } from "react";
import { Autocomplete, Button, Grid, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";

import { useUtilityQuery } from "apis/Utility";
import { complaintStatus, severityOptions } from "common/enums";
import { useSelector } from "react-redux";
import { SUPER_ADMIN } from "common/constant";

const initialFilters = {
  complaintStatus: { value: "", label: "All" },
  severityStatus: { value: "", label: "All" },
  startDate: null,
  endDate: null,
  utilityName: { value: "", label: "All" },
};

const FilterComplaintList = ({ setQuery, resetPage = () => {}, t }) => {
  const [selectedFilteredOption, setSelectedFilteredOption] =
    useState(initialFilters);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [utilityOptions, setUtilityOptions] = useState([]);
  const [invalidDateMessage, setInvalidDateMessage] = useState({
    startDateErrorMessage: "",
    endDateErrorMessage: "",
  });

  const { roles } = useSelector((state) => state.Login);
  const { data: { data: { items: utilityList } = {} } = {} } =
    useUtilityQuery();

  const handleSearch = () => {
    const { complaintStatus, severityStatus, startDate, endDate, utilityName } =
      selectedFilteredOption;
    resetPage();
    setQuery({
      complaintStatus: complaintStatus.value || "",
      severityStatus: severityStatus.value || "",
      fromDate: startDate?.format("MM-DD-YYYY") || "",
      toDate: endDate?.format("MM-DD-YYYY") || "",
      utilityId: utilityName.value || "",
    });
  };

  const handleClear = () => {
    resetPage();
    setSelectedFilteredOption(initialFilters);
    setQuery({});
  };

  const errorMessage = (error) => {
    switch (error) {
      case "maxDate":
        return t("Please select date that is before end date");
      case "minDate":
        return t("Please select date that is after start date");
      case "disableFuture":
        return t("You can't select future date");
      case "invalidDate":
        return t("Please enter a valid date");
      case "min1900":
        return t("Select a date that is subsequent than 01/01/1900");
      default:
        return "";
    }
  };

  useEffect(() => {
    if (utilityList) {
      const options = utilityList.map(({ name, id }) => ({
        label: name,
        value: id,
      }));
      setUtilityOptions([{ label: "All", value: "" }, ...options]);
    }
  }, [utilityList]);

  useEffect(() => {
    setStatusOptions([{ value: "", label: "All" }, ...complaintStatus]);
    setPriorityOptions([{ value: "", label: "All" }, ...severityOptions]);
  }, []);

  return (
    <Grid container rowSpacing={1} columnSpacing={1} className="mt-2 mb-3">
      <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
        <Autocomplete
          className="w-100 "
          size="small"
          value={selectedFilteredOption.complaintStatus}
          disableClearable
          options={statusOptions || []}
          renderInput={(params) => (
            <TextField {...params} label={t("Complaint Status")} />
          )}
          onChange={(_, option) =>
            setSelectedFilteredOption((prevState) => ({
              ...prevState,
              complaintStatus: option,
            }))
          }
          renderOption={(props, option) => {
            return (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            );
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
        <Autocomplete
          className="w-100 "
          size="small"
          value={selectedFilteredOption.severityStatus}
          disableClearable
          options={priorityOptions || []}
          renderInput={(params) => (
            <TextField {...params} label={t("Priority")} />
          )}
          onChange={(_, option) =>
            setSelectedFilteredOption((prevState) => ({
              ...prevState,
              severityStatus: option,
            }))
          }
          renderOption={(props, option) => {
            return (
              <li {...props} key={option.value}>
                {option?.label}
              </li>
            );
          }}
        />
      </Grid>
      {roles.includes(SUPER_ADMIN) && (
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
          <Autocomplete
            className="w-100 "
            size="small"
            value={selectedFilteredOption.utilityName}
            disableClearable
            options={utilityOptions || []}
            renderInput={(params) => (
              <TextField {...params} label={t("Utility")} />
            )}
            onChange={(_, option) =>
              setSelectedFilteredOption((prevState) => ({
                ...prevState,
                utilityName: option,
              }))
            }
            renderOption={(props, option) => {
              return (
                <li {...props} key={option?.value}>
                  {option?.label}
                </li>
              );
            }}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
        <DatePicker
          label={t("Start Date")}
          className="w-100"
          format="DD-MM-YYYY"
          value={selectedFilteredOption.startDate}
          disableFuture
          onError={(error) =>
            setInvalidDateMessage((prevState) => ({
              ...prevState,
              startDateErrorMessage: error === "minDate" ? "min1900" : error,
            }))
          }
          slotProps={{
            textField: {
              size: "small",
              helperText: errorMessage(
                invalidDateMessage.startDateErrorMessage
              ),
            },
          }}
          onChange={(date) => {
            setSelectedFilteredOption((prevState) => ({
              ...prevState,
              startDate: date,
            }));
          }}
          maxDate={selectedFilteredOption.endDate}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
        <DatePicker
          label={t("End Date")}
          className="w-100"
          format="DD-MM-YYYY"
          value={selectedFilteredOption.endDate}
          disableFuture
          minDate={selectedFilteredOption.startDate}
          onError={(error) =>
            setInvalidDateMessage((prevState) => ({
              ...prevState,
              endDateErrorMessage: error,
            }))
          }
          slotProps={{
            textField: {
              size: "small",
              helperText: errorMessage(invalidDateMessage.endDateErrorMessage),
            },
            actionBar: {
              actions: ["clear"],
            },
          }}
          onChange={(date) => {
            setSelectedFilteredOption((prevState) => ({
              ...prevState,
              endDate: date,
            }));
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
        <Button
          variant="outlined"
          className="me-2"
          onClick={handleSearch}
          disabled={
            !!(
              invalidDateMessage.startDateErrorMessage ||
              invalidDateMessage.endDateErrorMessage
            )
          }
        >
          {t("Search")}
        </Button>
        <Button variant="outlined" onClick={handleClear}>
          {t("Clear")}
        </Button>
      </Grid>
    </Grid>
  );
};

FilterComplaintList.propTypes = {
  setQuery: PropTypes.func,
  resetPage: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(FilterComplaintList);
